// src/lib/queryClient.ts — the app's single QueryClient (FORM-0016).
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';

/**
 * Racing data is written by overnight ETL, not by users, so a horse's form or a
 * completed meeting does not change while somebody is looking at it. The
 * defaults are tuned for that: cache generously, and do not refetch on every
 * window focus — it is the wrong trade for a form-study app where the user
 * tabs away to a video replay and back constantly.
 */
export const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes
export const DEFAULT_GC_TIME = 30 * 60 * 1000; // 30 minutes

/**
 * Auth failures are terminal: api/client.ts already refreshes the token once and
 * redirects to /login when that fails, so retrying a 401/403 just delays the
 * redirect. 4xx in general means the request was wrong, not unlucky.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        gcTime: DEFAULT_GC_TIME,
        refetchOnWindowFocus: false,
        retry: shouldRetry,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();

export default queryClient;
